/**
 * <s-night-sky>: the hero's night before the sunrise. A sparse field of faint, slowly twinkling stars in the
 * upper sky and, now and then, one shooting star (first ~1.4s after arrival, then every 4–9s at random) that
 * streaks down-left with a white-to-coral tail. Fades out as the closest <s-hero-reveal>'s --sun rises (day
 * comes). Extras: once per visit a constellation draws a faint "4" (The4) top-right and dissolves; every ~30s a
 * bigger golden "wish" meteor dives toward the horizon and sets [data-wish] on the hero (or the closest [data-wish-host], e.g. the contact sky) so the horizon flares coral.
 * Canvas, capped DPR, paused off screen / hidden tab. Reduced motion: still stars, no meteors or constellation.
 */
class SNightSky extends HTMLElement {
  #drawnAt = 0;
  #cleared = false;
  #canvas = null;
  #ctx = null;
  #stars = [];
  #meteors = [];
  #w = 0;
  #h = 0;
  #dpr = 1;
  #raf = 0;
  #visible = false;
  #reduced = false;
  #nextMeteor = 0;
  #nextWish = 0;
  #constellation = null;
  #io = null;
  #ro = null;
  #onVisibility = () => this.#wake();

  connectedCallback() {
    this.#canvas = document.createElement('canvas');
    this.#canvas.setAttribute('aria-hidden', 'true');
    this.#canvas.style.cssText = 'width:100%;height:100%;display:block';
    this.append(this.#canvas);
    this.#ctx = this.#canvas.getContext('2d');
    this.#reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const start = performance.now();
    this.#nextMeteor = start + 1400;
    this.#nextWish = start + 9000;
    this.#initConstellation(start);
    this.#ro = new ResizeObserver(() => this.#resize());
    this.#ro.observe(this);
    this.#io = new IntersectionObserver(([entry]) => {
      this.#visible = entry.isIntersecting;
      this.#wake();
    });
    this.#io.observe(this);
    document.addEventListener('visibilitychange', this.#onVisibility);
  }

  disconnectedCallback() {
    this.#io?.disconnect();
    this.#ro?.disconnect();
    document.removeEventListener('visibilitychange', this.#onVisibility);
    cancelAnimationFrame(this.#raf);
    this.#raf = 0;
  }

  #resize() {
    const rect = this.getBoundingClientRect();
    this.#dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.#w = rect.width;
    this.#h = rect.height;
    this.#canvas.width = Math.round(rect.width * this.#dpr);
    this.#canvas.height = Math.round(rect.height * this.#dpr);
    const count = Math.round(Math.min(90, (rect.width * rect.height) / 14000));
    this.#stars = Array.from({ length: count }, () => {
      const y = Math.pow(Math.random(), 1.6) * 0.62;
      return {
        x: Math.random(),
        y,
        r: Math.random() < 0.12 ? 1.1 + Math.random() * 0.5 : 0.5 + Math.random() * 0.5,
        a: (0.25 + Math.random() * 0.5) * (1 - y * 0.9),
        period: 2400 + Math.random() * 4200,
        phase: Math.random() * Math.PI * 2,
        warm: Math.random() < 0.15,
      };
    });
    if (this.#reduced) this.#draw(performance.now());
  }

  #wake() {
    if (this.#reduced) {
      this.#draw(performance.now());
      return;
    }
    if (this.#visible && !document.hidden && !this.#raf) {
      this.#raf = requestAnimationFrame((t) => this.#tick(t));
    }
  }

  #night() {
    const host = this.closest('s-hero-reveal');
    const sun = parseFloat(host?.style.getPropertyValue('--sun')) || 0;
    return Math.max(0, 1 - sun * 1.6);
  }

  #spawn(now) {
    const fromRight = Math.random() < 0.7;
    const angle = (fromRight ? 200 + Math.random() * 22 : 318 + Math.random() * 22) * (Math.PI / 180);
    const speed = (this.#w / 1400) * (0.9 + Math.random() * 0.5);
    this.#meteors.push({
      x: fromRight ? this.#w * (0.45 + Math.random() * 0.5) : this.#w * (0.05 + Math.random() * 0.4),
      y: this.#h * (0.02 + Math.random() * 0.22),
      vx: Math.cos(angle),
      vy: -Math.sin(angle),
      speed: Math.max(0.55, speed),
      length: 90 + Math.random() * 120,
      born: now,
      life: 900 + Math.random() * 500,
    });
    this.#nextMeteor = now + 4000 + Math.random() * 5000;
  }

  /** Once per visit: a few seconds in, stars join into a faint "4" (The4) and dissolve. */
  #initConstellation(start) {
    if (this.#reduced) return;
    try {
      if (sessionStorage.getItem('the4-constellation')) return;
      sessionStorage.setItem('the4-constellation', '1');
    } catch {}
    const A = [0.62, 0], M1 = [0.31, 0.31], B = [0, 0.62], K = [0.62, 0.62], C = [0.92, 0.62], M3 = [0.62, 0.31], E = [0.62, 1];
    this.#constellation = {
      start: start + 2600,
      points: [A, M1, B, K, C, M3, E],
      lines: [[A, M1], [M1, B], [B, K], [K, C], [A, M3], [M3, K], [K, E]],
    };
  }

  #drawConstellation(now, night) {
    const c = this.#constellation;
    if (!c || now < c.start) return;
    const t = (now - c.start) / 1000;
    const total = 4.6;
    if (t > total) {
      this.#constellation = null;
      return;
    }
    const ctx = this.#ctx;
    const size = Math.min(130, this.#h * 0.18, this.#w * 0.26);
    const ox = this.#w * (this.#w > 768 ? 0.8 : 0.7) - size * 0.46;
    const oy = this.#h * 0.11;
    const out = t > total - 1.1 ? (total - t) / 1.1 : 1;
    const pointIn = Math.min(1, t / 0.6);
    const xy = ([x, y]) => [ox + x * size, oy + y * size];

    ctx.lineCap = 'round';
    ctx.lineWidth = 0.8;
    c.lines.forEach(([p, q], i) => {
      const local = Math.max(0, Math.min(1, (t - 0.6 - i * 0.16) / 0.35));
      if (!local) return;
      const [x1, y1] = xy(p);
      const [x2, y2] = xy(q);
      ctx.strokeStyle = `rgba(255,190,180,${(0.42 * out * night).toFixed(3)})`;
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x1 + (x2 - x1) * local, y1 + (y2 - y1) * local);
      ctx.stroke();
    });
    for (const p of c.points) {
      const [x, y] = xy(p);
      const a = pointIn * out * night;
      const glow = ctx.createRadialGradient(x, y, 0, x, y, 7);
      glow.addColorStop(0, `rgba(255,255,255,${(0.9 * a).toFixed(3)})`);
      glow.addColorStop(0.35, `rgba(255,165,155,${(0.35 * a).toFixed(3)})`);
      glow.addColorStop(1, 'rgba(255,132,120,0)');
      ctx.fillStyle = glow;
      ctx.beginPath();
      ctx.arc(x, y, 7, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  /** Every ~30s: a bigger golden "wish" meteor dives toward the horizon, which flares coral as it lands. */
  #spawnWish(now) {
    const angle = (206 + Math.random() * 8) * (Math.PI / 180);
    this.#meteors.push({
      x: this.#w * (0.78 + Math.random() * 0.12),
      y: this.#h * 0.04,
      vx: Math.cos(angle),
      vy: -Math.sin(angle),
      speed: Math.max(0.6, this.#w / 1500),
      length: 260,
      born: now,
      life: 1700,
      wish: true,
    });
    this.#nextWish = now + 28000 + Math.random() * 8000;
    this.#nextMeteor = Math.max(this.#nextMeteor, now + 3500);
    const host = this.closest('s-hero-reveal, [data-wish-host]');
    window.setTimeout(() => {
      if (!host) return;
      host.removeAttribute('data-wish');
      void host.offsetWidth;
      host.setAttribute('data-wish', '');
      window.setTimeout(() => host.removeAttribute('data-wish'), 1800);
    }, 1150);
  }

  #tick(now) {
    this.#raf = 0;
    if (!this.#visible || document.hidden) return;
    // Perf (2026-10-29 audit): ~30 fps is plenty for slow twinkles and time-based meteors; once the sun is up and
    // the sky is clear, nothing is drawn at all.
    if (now - this.#drawnAt < 32) { this.#raf = requestAnimationFrame((t) => this.#tick(t)); return; }
    this.#drawnAt = now;
    if (this.#night() <= 0 && this.#cleared) { this.#raf = requestAnimationFrame((t) => this.#tick(t)); return; }
    this.#cleared = this.#night() <= 0;
    const night = this.#night();
    if (night > 0.5 && now >= this.#nextWish && !this.#meteors.some((m) => m.wish)) this.#spawnWish(now);
    if (night > 0.05 && now >= this.#nextMeteor && this.#meteors.length < 2) this.#spawn(now);
    this.#draw(now);
    this.#raf = requestAnimationFrame((t) => this.#tick(t));
  }

  #draw(now) {
    const ctx = this.#ctx;
    if (!ctx) return;
    ctx.setTransform(this.#dpr, 0, 0, this.#dpr, 0, 0);
    ctx.clearRect(0, 0, this.#w, this.#h);
    const night = this.#night();
    if (night <= 0) {
      this.#meteors = [];
      return;
    }

    for (const star of this.#stars) {
      const twinkle = this.#reduced ? 1 : 0.65 + 0.35 * Math.sin(now / star.period + star.phase);
      ctx.fillStyle = star.warm ? `rgba(255,214,200,${(star.a * twinkle * night).toFixed(3)})` : `rgba(255,255,255,${(star.a * twinkle * night).toFixed(3)})`;
      ctx.beginPath();
      ctx.arc(star.x * this.#w, star.y * this.#h, star.r, 0, Math.PI * 2);
      ctx.fill();
    }

    this.#drawConstellation(now, night);

    this.#meteors = this.#meteors.filter((m) => now - m.born < m.life);
    for (const m of this.#meteors) {
      const age = (now - m.born) / m.life;
      const travelled = (now - m.born) * m.speed;
      const hx = m.x + m.vx * travelled;
      const hy = m.y + m.vy * travelled;
      const tail = m.length * Math.min(1, age * 3);
      const tx = hx - m.vx * tail;
      const ty = hy - m.vy * tail;
      const fade = (age < 0.15 ? age / 0.15 : 1 - Math.pow((age - 0.15) / 0.85, 1.6)) * night;
      const grad = ctx.createLinearGradient(hx, hy, tx, ty);
      grad.addColorStop(0, `rgba(255,255,255,${(0.95 * fade).toFixed(3)})`);
      grad.addColorStop(0.25, m.wish ? `rgba(255,214,160,${(0.8 * fade).toFixed(3)})` : `rgba(255,165,155,${(0.55 * fade).toFixed(3)})`);
      grad.addColorStop(1, 'rgba(255,132,120,0)');
      ctx.strokeStyle = grad;
      ctx.lineWidth = m.wish ? 2.4 : 1.4;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(hx, hy);
      ctx.lineTo(tx, ty);
      ctx.stroke();
      const glowR = m.wish ? 11 : 6;
      const glow = ctx.createRadialGradient(hx, hy, 0, hx, hy, glowR);
      glow.addColorStop(0, `rgba(255,255,255,${(0.9 * fade).toFixed(3)})`);
      glow.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = glow;
      ctx.beginPath();
      ctx.arc(hx, hy, glowR, 0, Math.PI * 2);
      ctx.fill();
    }
  }
}

if (!customElements.get('s-night-sky')) customElements.define('s-night-sky', SNightSky);
